import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, EllipsisHorizontalIcon, EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/16/solid';
import { ArrowUpTrayIcon } from '@heroicons/react/20/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from '../../../components/Dropdown';
import { Link } from '../../../components/Link';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useAiReportCustomerProfiles } from '../../../hooks/useAiReportCustomerProfile';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import { CUSTOMER_PROFILE_FORM_FIELDS } from './formFields';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const CustomerProfilesPage: React.FC = () => {
    const { profiles, loading, error, fetchProfiles, deleteProfile, importProfiles } = useAiReportCustomerProfiles();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage] = useState<number>(10);
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const navigate = useNavigate();
    const importInputRef = useRef<HTMLInputElement>(null);
    const [sortOrder, setSortOrder] = useState('az');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterState, setFilterState] = useState('');

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterState('');
    };

    const availableYears = Array.from(
        new Set(
            profiles
                .map((p) => new Date(p.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const availableStates = Array.from(
        new Set(profiles.map((p) => p.state).filter((s): s is string => !!s))
    ).sort();

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterState].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterState ? [{ key: 'state', label: `State: ${filterState}`, onClear: () => setFilterState('') }] : []),
    ];

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file next time
        if (!file) return;

        setIsImporting(true);
        try {
            const result = await importProfiles(file);
            const parts = [`${result.created} created`, `${result.updated} updated`];
            if (result.skippedRows.length > 0) {
                parts.push(`${result.skippedRows.length} skipped (missing Customer Name)`);
            }
            showSuccessToast(`Import complete: ${parts.join(', ')}`);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.error || 'Failed to import customer profiles');
        } finally {
            setIsImporting(false);
        }
    };

    const filtered = profiles
        .filter((p) => {
            const matchesSearch = p.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
                || (p.customer_code || '').toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStartDate = !filterStartDate || new Date(p.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(p.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(p.created_time).getFullYear().toString() === filterYear;
            const matchesState = !filterState || p.state === filterState;
            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesState;
        })
        .sort((a, b) => sortOrder === 'az' ? a.customer_name.localeCompare(b.customer_name) : b.customer_name.localeCompare(a.customer_name));

    const indexOfLast = currentPage * perPage;
    const indexOfFirst = indexOfLast - perPage;
    const currentProfiles = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / perPage);

    const handleOpenDeleteDialog = (id: number) => {
        setSelectedId(id);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (selectedId === null) return;
        setIsDeleting(true);
        try {
            await deleteProfile(selectedId);
            showSuccessToast('Customer profile deleted');
            setIsOpen(false);
        } catch (err) {
            showErrorToast('Failed to delete customer profile');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <RouterLink
                to="/gp/ai-report-helper"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to AI Report Helper
            </RouterLink>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Customer Profiles</Heading>
                    <p className="mt-2 text-xs text-zinc-500">
                        Upload Excel columns: {CUSTOMER_PROFILE_FORM_FIELDS.map((f) => f.label).join(', ')} (Customer Name required;
                        matching Customer Code updates the existing profile instead of creating a duplicate).
                    </p>
                </div>
                <div className="flex gap-3">
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        onChange={handleImportFileChange}
                    />
                    <Dropdown>
                        <DropdownButton outline>
                            <EllipsisHorizontalIcon />
                            More
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                            <DropdownItem onClick={handleImportClick} disabled={isImporting}>
                                <ArrowUpTrayIcon />
                                <DropdownLabel>{isImporting ? 'Importing...' : 'Upload Excel'}</DropdownLabel>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                    <Button onClick={() => navigate('/gp/ai-report-helper/customer-profiles/add')}>
                        <PlusIcon />
                        Create new profile
                    </Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search by name or code…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {!loading && !error && currentProfiles.length === 0 && <p className='py-16 text-center'>No customer profiles found.</p>}
                {!loading && !error && currentProfiles.map((profile, index) => (
                    <li key={profile.id} className="rounded-xl hover:bg-secondary">
                        <div className="flex items-center justify-between px-2">
                            <div className="flex gap-6 py-5">
                                <Avatar square initials={profile.customer_name[0]} className="bg-zinc-300 text-gray-700 size-12" />
                                <div>
                                    <div className="text-base/6 font-semibold">
                                        <Link href={`/gp/ai-report-helper/customer-profiles/edit/${profile.id}`}>
                                            {profile.customer_name}
                                        </Link>
                                    </div>
                                    <div className="text-xs/6 text-zinc-600">
                                        {profile.customer_code || 'No code'}
                                        {profile.district ? ` · ${profile.district}` : ''}
                                        {profile.state ? `, ${profile.state}` : ''}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <Dropdown>
                                    <DropdownButton plain aria-label="More options">
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom end">
                                        <DropdownItem onClick={() => navigate(`/gp/ai-report-helper/customer-profiles/edit/${profile.id}`)}>
                                            Edit
                                        </DropdownItem>
                                        <DropdownItem onClick={() => handleOpenDeleteDialog(profile.id)}>
                                            Delete
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalResult={filtered.length}
                dataName='Customer Profiles'
            />
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {availableStates.length > 0 && (
                    <div>
                        <Text>By State</Text>
                        <Select value={filterState} onChange={(e) => setFilterState(e.target.value)}>
                            <option value="">All States</option>
                            {availableStates.map((state) => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Customer Profile</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this customer profile? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomerProfilesPage;
